import React, { useState } from 'react';
import { Effect } from 'effect';
import { HttpBody } from '@effect/platform';
import { I18n, Auth } from 'aws-amplify';
import { redirectToApp } from 'amplify/services';
import Api from 'services/api';
import { useToast } from 'contexts/toastContext';
import { useAuthorizedUserContext } from 'contexts/authorizedUserContext';

import { useRuntime } from '../../effect/useRuntime';
import PhoneRegistration from './components/PhoneRegistration';
import PhoneVerification from './components/PhoneVerification';
import Layout from './components/Layout';
import 'components/Authenticator/helpers/styles.css';

const PhoneChangePage: React.FC = () => {
  const { authorizedUserContext, setAuthorizedUserContext } = useAuthorizedUserContext();
  const [currentStep, setCurrentStep] = useState<'registration' | 'verification'>('registration');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [numberValidationError, setNumberValidationError] = useState<string | false>(false);
  const [codeValidationError, setCodeValidationError] = useState<string | false>(false);
  const runtime = useRuntime();
  const { setToastData } = useToast();

  const dismissCodeValidationError = React.useCallback(() => {
    setCodeValidationError(false);
  }, [setCodeValidationError]);

  const updatePhone = async (phone: string) => {
    setPhoneNumber(phone);
    Api.use((service) => service.updatePhone({ body: HttpBody.unsafeJson({ phone }) })).pipe(
      Effect.catchTag('ValidatedResponseError', (error) => {
        // error.validatedBody is now typed as GenericResponseError!
        const errorMessage = error.validatedBody.error || I18n.get('phoneVerification.step2.validation.genericError');
        setNumberValidationError(errorMessage);
        return Effect.fail(error);
      }),
      Effect.map((result) => {
        setCurrentStep('verification');
        return result;
      }),
      Effect.catchAll(() => {
        return Effect.succeed(void 0);
      }),
      runtime.runPromise,
    );
  };

  const handleSkip = async () => {
    Api.use((service) => service.skipPhoneUpdate({ body: HttpBody.unsafeJson({ skip: true }) })).pipe(
      Effect.timeout('5 seconds'),
      Effect.tapError((error) => Effect.log(`Skip phone update error: ${error}`)),
      Effect.ignore,
      Effect.tap(() => redirectToApp(authorizedUserContext.appUrl)),
      runtime.runPromise,
    );
  };

  const handleResendCode = () => {
    return Api.use((service) => service.sendCode()).pipe(
      Effect.matchEffect({
        onFailure: (error) =>
          Effect.sync(() => {
            const errorMessage =
              error._tag === 'ValidatedResponseError'
                ? error.validatedBody.error || I18n.get('phoneVerification.step2.validation.genericError')
                : I18n.get('phoneVerification.step2.validation.genericError');

            setCodeValidationError(errorMessage);
          }),
        onSuccess: () =>
          Effect.sync(() => {
            const message = `${I18n.get('phoneVerification.step2.codeSentTo')} \u2066${phoneNumber}\u2069`;
            setToastData({
              variant: 'success',
              message,
              heading: '',
            });
          }),
      }),
      runtime.runPromise,
    );
  };

  const handleVerifyCode = (code: string) => {
    return Api.use((service) => service.verifyCode({ body: HttpBody.unsafeJson({ code }) })).pipe(
      Effect.catchTag('ValidatedResponseError', (error) => {
        const errorMessage = error.validatedBody.error || I18n.get('phoneVerification.step2.validation.codeError');
        setCodeValidationError(errorMessage);
        return Effect.fail(error);
      }),
      Effect.map((result) => {
        redirectToApp(authorizedUserContext.appUrl);
        return result;
      }),
      Effect.catchAll(() => {
        return Effect.succeed(void 0);
      }),
      runtime.runPromise,
    );
  };

  const handleBackToRegistration = () => {
    dismissCodeValidationError();
    setCurrentStep('registration');
  };

  const handleBackToSignIn = async () => {
    try {
      await Auth.signOut();
      setAuthorizedUserContext({
        ...authorizedUserContext,
        // forces user to login page from other screens
        phoneVerificationRequired: false,
      });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <Layout>
      {currentStep === 'registration' ? (
        <PhoneRegistration
          onUpdatePhone={updatePhone}
          validationError={numberValidationError}
          onSkip={handleSkip}
          onBackToSignIn={handleBackToSignIn}
        />
      ) : (
        <PhoneVerification
          onVerify={handleVerifyCode}
          onSkip={handleSkip}
          onResendCode={handleResendCode}
          onBackToRegistration={handleBackToRegistration}
          onBackToSignIn={handleBackToSignIn}
          phoneNumber={phoneNumber}
          validationError={codeValidationError}
          dismissValidationError={dismissCodeValidationError}
        />
      )}
    </Layout>
  );
};

export default PhoneChangePage;
